import React from "react";
import { Link } from "react-router-dom";
import { Heart, Plus, Star } from "lucide-react";
import { img, money } from "../lib/api";
import { useStore } from "../context/StoreContext";

export default function ProductCard({ product, index = 0 }) {
  const { addToCart, wishlist, toggleWishlist } = useStore();
  const imgs = product.images || [];
  const primary = img(imgs[0] || imgs[1]);
  const secondary = img(imgs[1] || imgs[0]);
  const wished = wishlist.includes(product.slug);
  const discount = product.mrp > product.price
    ? Math.round(((product.mrp - product.price) / product.mrp) * 100) : 0;
  const tint = { face: "bg-sage/25", hair: "bg-[#E7E1D3]", body: "bg-[#EAE4E6]", fragrance: "bg-[#F1E4E0]", wellness: "bg-moss/10" }[product.category] || "bg-beige/40";

  return (
    <div className="group flex flex-col card-lift" data-testid={`product-card-${product.slug}`}>
      <div className={`relative overflow-hidden ${tint} aspect-[4/5]`}>
        <Link to={`/product/${product.slug}`} aria-label={product.name}>
          <img src={primary} alt={product.name}
            className="absolute inset-0 h-full w-full object-cover transition-opacity duration-700 group-hover:opacity-0" loading="lazy" />
          <img src={secondary} alt=""
            className="absolute inset-0 h-full w-full object-cover scale-105 opacity-0 transition-opacity duration-700 group-hover:opacity-100" loading="lazy" />
        </Link>
        <div className="absolute left-3 top-3 flex flex-col gap-2">
          {product.is_bestseller && <span className="bg-forest text-ivory text-[10px] tracking-[0.15em] uppercase px-2.5 py-1">Bestseller</span>}
          {product.is_new && <span className="bg-ivory text-forest text-[10px] tracking-[0.15em] uppercase px-2.5 py-1 border border-forest/20">New</span>}
          {discount > 0 && <span className="bg-brown text-ivory text-[10px] tracking-[0.15em] uppercase px-2.5 py-1">-{discount}%</span>}
        </div>
        <button onClick={() => toggleWishlist(product.slug)}
          data-testid={`wishlist-toggle-${product.slug}`}
          className="absolute right-3 top-3 h-9 w-9 flex items-center justify-center bg-ivory/80 backdrop-blur hover:bg-ivory transition-colors">
          <Heart size={16} className={wished ? "fill-forest text-forest" : "text-forest"} strokeWidth={1.4} />
        </button>
        <button onClick={() => addToCart(product, 1)}
          data-testid={`quick-add-${product.slug}`}
          className="absolute bottom-0 left-0 right-0 translate-y-full group-hover:translate-y-0 transition-transform duration-500 bg-mint text-forest text-xs uppercase tracking-[0.18em] py-3.5 flex items-center justify-center gap-2 font-medium">
          <Plus size={14} strokeWidth={1.6} /> Quick Add
        </button>
      </div>
      <div className="pt-4 flex flex-col gap-1">
        {product.review_count > 0 && (
          <div className="flex items-center gap-1 text-forest/60">
            <Star size={12} className="fill-forest/70 text-forest/70" />
            <span className="text-xs">{product.rating} ({product.review_count})</span>
          </div>
        )}
        <Link to={`/product/${product.slug}`}>
          <h3 className="serif text-xl leading-tight text-forest">{product.name}</h3>
        </Link>
        <p className="text-sm text-forest/55 line-clamp-1">{product.short}</p>
        <div className="flex items-baseline gap-2 pt-1">
          <span className="text-forest font-medium">{money(product.price)}</span>
          {discount > 0 && <span className="text-forest/40 line-through text-sm">{money(product.mrp)}</span>}
        </div>
      </div>
    </div>
  );
}
